import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../helpers/api';
import { ENDPOINTS } from '../helpers/constants';

/**
 * Reusable hook for handling order cancellation requests
 * @param {Object} options
 * @param {Function} options.onSuccess - Callback to execute after successful set/unset
 * @param {Function} [options.onSubmitStart] - Called when a set/undo request starts
 * @param {Function} [options.onSubmitEnd] - Called when a set/undo request ends
 * @returns {Object} cancellation state and handlers
 */
const useCancellationRequest = ({
  onSuccess,
  onSubmitStart,
  onSubmitEnd,
  viewAsOrganizationId
} = {}) => {
  const [state, setState] = useState({
    isOpen: false,
    submitting: false,
    order: null,
    service: null,
    statementId: null
  });

  const open = useCallback((order, service, statementId = null) => {
    if (order.cancellationRequested) {
      toast.error('Cancellation has already been requested for this order');
      return;
    }

    setState({
      isOpen: true,
      submitting: false,
      order: {
        _id: order._id || order.orderId,
        orderType: order.orderType,
        note: '',
        fileIds: []
      },
      service: { driveFolder: service?.driveFolder || service?.serviceDriveFolder },
      statementId
    });
  }, []);

  const close = useCallback(() => {
    setState({
      isOpen: false,
      submitting: false,
      order: null,
      service: null,
      statementId: null
    });
  }, []);

  const updateOrderField = useCallback((key, value) => {
    setState(prev => ({
      ...prev,
      order: prev.order ? { ...prev.order, [key]: value } : null
    }));
  }, []);

  const submit = useCallback(async () => {
    if (!state.order) return;

    setState(prev => ({ ...prev, submitting: true }));
    onSubmitStart?.();

    try {
      // Use the existing order cancellation endpoint
      // The Order model pre-save hook will automatically sync the invoice statement
      const endpoint = viewAsOrganizationId
        ? `${ENDPOINTS.ORDER_REQUEST_CANCELLATION(state.order._id)}?viewAsOrganizationId=${encodeURIComponent(
            viewAsOrganizationId
          )}`
        : ENDPOINTS.ORDER_REQUEST_CANCELLATION(state.order._id);

      const { error } = await makeRESTApiRequest({
        endpoint,
        method: 'POST',
        requestBody: [
          {
            _id: state.order._id,
            fileIds: state.order.fileIds,
            note: state.order.note,
            cancellationRequested: true
          }
        ]
      });

      if (error) {
        setState(prev => ({ ...prev, submitting: false }));
        toast.error(error);
        return;
      }

      toast.success('Cancellation request submitted successfully');
      onSuccess?.({
        orderId: state.order._id,
        statementId: state.statementId,
        cancellationRequested: true
      });
      close();
    } catch (error) {
      setState(prev => ({ ...prev, submitting: false }));
      toast.error('An unexpected error occurred');
    } finally {
      onSubmitEnd?.();
    }
  }, [
    state.order,
    state.statementId,
    close,
    onSuccess,
    onSubmitStart,
    onSubmitEnd,
    viewAsOrganizationId
  ]);

  const undo = useCallback(
    async (order, statementId = null) => {
      const orderId = order?._id || order?.orderId;
      if (!orderId) return;

      setState(prev => ({ ...prev, submitting: true }));
      onSubmitStart?.();

      try {
        const endpoint = viewAsOrganizationId
          ? `${ENDPOINTS.ORDER_REQUEST_CANCELLATION(orderId)}?viewAsOrganizationId=${encodeURIComponent(
              viewAsOrganizationId
            )}`
          : ENDPOINTS.ORDER_REQUEST_CANCELLATION(orderId);

        const { error } = await makeRESTApiRequest({
          endpoint,
          method: 'POST',
          requestBody: [
            {
              _id: orderId,
              cancellationRequested: false
            }
          ]
        });

        if (error) {
          setState(prev => ({ ...prev, submitting: false }));
          toast.error(error);
          return;
        }

        toast.success('Cancellation request undone successfully');
        onSuccess?.({
          orderId,
          statementId,
          cancellationRequested: false
        });
        setState(prev => ({ ...prev, submitting: false }));
      } catch (error) {
        setState(prev => ({ ...prev, submitting: false }));
        toast.error('An unexpected error occurred');
      } finally {
        onSubmitEnd?.();
      }
    },
    [onSuccess, onSubmitStart, onSubmitEnd, viewAsOrganizationId]
  );

  return {
    state,
    open,
    close,
    updateOrderField,
    submit,
    undo
  };
};

export default useCancellationRequest;
